import React, { useEffect, useState } from 'react';
import { Table, Button, Badge, Row, Container } from 'react-bootstrap';
import { useTheme } from '../../context/PortalThemeContext';
import { useNavigate } from 'react-router-dom';
import { useRouteContext } from '../../context/RouteContext';
import { ShopifyCard } from './ProductVariantCards';
import { ScreenSize } from './ScreenSize';
import { fetchWithSessionToken } from './fetchWithSessionToken';
import { fetchDynamicOrder } from '../../utils/orderLookupApi';
import { ProductCarousel } from './ProductCarousel';
import { CHAT_API_BASE } from '../../config/api';


export default function OrderHistory() {
  const {
    customer,
    setCustomer,
    setOrderDetails,
    trackingInfo,
    setTrackingInfo,
    orderDetails,
    shopifyProducts,
  } = useRouteContext();

  const getFriendlyStatus = (status) => {
    const statusMap = {
      // Claim statuses
      NOT_REVIEWED: 'Not Reviewed',
      IN_REVIEW: 'In Review',
      DENIED: 'Denied',
      REQUEST: 'Additional Info Requested',
      RESOLVED: 'Resolved',

      // Return statuses
      // OPEN statuses
      REQUESTED: 'Requested',
      PROCESSING: 'Processing',
      CUSTOMER_CANCELLED: 'Customer Cancelled',
      CANCELLED: 'Cancelled',
      REJECTED: 'Rejected',
      REFUNDED: 'Refunded',
      EXCHANGED: 'Exchanged',
      EXPIRED: 'Expired',

      // Shared
      APPROVED: 'Approved',
      RESOLVED: 'Resolved',
    };

    return statusMap[status] || status;
  };

  const goToOrderSummary = async (order, type) => {
    try {
      const response = await fetchDynamicOrder(
        {
          companyId: customer.companyId,
          searchParam: order.orderNumber,
          additionalParam: order.customerEmail || order.customerPhone || order.customerZip,
        },
        { withSession: true, headers: { 'X-User-Email': customer.email } }
      );

      if (response.ok) {
        const data = await response.json();
        const enrichedOrder = data.order;
        enrichedOrder.orderId = order.orderNumber;
        enrichedOrder.orderReturns = data.orderReturns;
        enrichedOrder.orderClaims = data.orderClaims;
        if (data.trackingInfo) enrichedOrder.trackingInfo = data.trackingInfo;
        enrichedOrder.shopifyProducts = data.shopifyProducts;
        enrichedOrder.bearerToken = data.bearerToken;

        localStorage.setItem('brella_order', JSON.stringify(enrichedOrder));
        window.location.href = type === 'return' ? '/apps/brella/returns' : '/apps/brella/claims';
      } else {
        console.error('Failed to fetch full order.');
      }
    } catch (error) {
      console.error(`Error loading ${type}:`, error);
    }
  };

  const theme = useTheme();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!customer?.email || !customer?.companyId) return;

      try {
        const url = new URL(
          `${CHAT_API_BASE}/companies/orders/summary`
        );
        url.searchParams.append('companyId', customer.companyId);
        url.searchParams.append('userEmail', customer.email);

        const response = await fetchWithSessionToken(url.toString(), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Email': customer.email,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setOrders(data);
        } else {
          console.error('Failed to fetch order summaries.');
        }
      } catch (err) {
        console.error('Error loading order summaries:', err);
      }
    };

    fetchOrders();
  }, [customer]);

  return (
    <div style={{ backgroundColor: theme.backgroundColor, height: '100vh' }}>
      <Container style={{ padding: '4rem 2rem', maxWidth: ScreenSize.LG, margin: '0 auto' }}>
        <h1 style={{ fontWeight: 500, marginBottom: '2rem', fontSize: '2.5rem' }}>Order History</h1>
        <Table responsive bordered hover className='order-table'>
          <thead>
            <tr className='medium-text'>
              <th>Order Date</th>
              <th>Order Number</th>
              <th>Type</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {orders.length > 0 ? (
              orders.map((order, idx) => (
                <tr key={idx}>
                  <td>{new Date(order.orderDate).toLocaleDateString()}</td>
                  <td>#{order.orderNumber}</td>
                  <td>{order.type}</td>
                  <td>
                    <Badge bg="secondary" style={{ borderRadius: '12px', padding: '0.5em 1em' }}>
                      {getFriendlyStatus(order.status)}
                    </Badge>
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        className="button-accent-light"
                        size="sm"
                        onClick={() => {
                          goToOrderSummary(order, 'return');
                        }}
                      >
                        {order.type === 'RETURN' ? 'View Return' : 'Start Return'}
                      </Button>

                      <Button
                        className="button-accent-light"
                        size="sm"
                        onClick={() => {
                          goToOrderSummary(order, 'claim');
                        }}
                      >
                        {order.type === 'CLAIM' ? 'View Claim' : 'File Claim'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center text-muted">
                  No orders found.
                </td>
              </tr>
            )}
          </tbody>

        </Table>

        {/* @TODO uncomment this and add this when we can add to cart */}
        {/* {shopifyProducts && (
          <>
            <h1 style={{ marginTop: '7rem', marginBottom: '2.5rem' }}>Based on your past orders</h1>
            <ProductCarousel items={
              [...shopifyProducts].map((product, index) => (
                <div key={index} className='embla__slide'>
                  <ShopifyCard shopifyProduct={product} onClick={() => { }} />
                </div>
              ))
            } />
          </>
        )} */}
      </Container>
    </div >
  );
};
